'use client';

import { useTranslations } from 'next-intl';
import { Box, Button, Heading, Modal, Paragraph } from '@zillow/constellation-10';
import { Link } from '@/src/i18n/navigation';
import { LoginForm } from '@/src/components/features/auth/LoginForm';
import { useModalDismissProps } from '@/src/lib/hooks/ui/useModalDismissProps';

type ListingActionSignInModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthenticated: () => void;
  postLoginRedirect: string;
  /** Modal heading. Defaults to the favorites prompt copy. */
  title?: string;
  /** Supporting paragraph. Defaults to the favorites prompt copy. */
  intro?: string;
  /** Test id on Modal.Content. Defaults to `favorite-sign-in-modal`. */
  testId?: string;
};

/**
 * Sign-in prompt shared by the login-gated actions (favorite, hide, SRP save search).
 * Copy + test id default to the favorites case and can be overridden per action.
 */
export function ListingActionSignInModal({
  open,
  onOpenChange,
  onAuthenticated,
  postLoginRedirect,
  title,
  intro,
  testId = 'favorite-sign-in-modal',
}: ListingActionSignInModalProps) {
  const modalDismissProps = useModalDismissProps();
  const t = useTranslations('auth');
  const heading = title ?? t('favoritePromptTitle');
  const introCopy = intro ?? t('favoritePromptIntro');

  return (
    <Modal.Root
      useDismissProps={modalDismissProps}
      open={open}
      onOpenChange={onOpenChange}
      size={{ base: 'fullScreen', lg: 'xs' }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content aria-label={heading} data-testid={testId}>
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                {heading}
              </Heading>
            </Modal.Header>
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                <Paragraph>{introCopy}</Paragraph>
                <LoginForm
                  onAuthenticated={() => onAuthenticated()}
                  onEmailError={(error) => console.error('[Auth] favorite modal auth flow mutation failed', error)}
                  onPasswordError={(error) =>
                    console.error('[Auth] favorite modal email/password mutation failed', error)
                  }
                  passwordFooter={
                    <Button asChild emphasis="bare" size="sm" tone="brand" fluid css={{ marginTop: 'default' }}>
                      <Link href="/forgot-password">{t('forgotPassword')}</Link>
                    </Button>
                  }
                  postLoginRedirect={postLoginRedirect}
                />
              </Box>
            </Modal.Body>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
